import React, { useState } from 'react';
import Dialog from '../Dialog/Dialog';
import { TextButton } from '../Button';
import Input from '../Input/Input';
import TextArea from '../TextArea/TextArea';
import languagesData from '../../data/languages.json';

interface TranslationKeyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (keyName: string, defaultValue: string) => void;
  isLoading?: boolean;
  firstLanguage: string | null;
}

const TranslationKeyDialog: React.FC<TranslationKeyDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  firstLanguage
}) => {
  const [keyName, setKeyName] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [errors, setErrors] = useState<{
    keyName?: string;
    defaultValue?: string;
  }>({});

  // Get language name from language code
  const getLanguageName = (langCode: string | null) => {
    if (!langCode) return 'No language';
    const language = languagesData.languages.find(
      lang => lang.code.toLowerCase() === langCode.toLowerCase() ||
             lang.code.toLowerCase().replace(/-/g, '_') === langCode.toLowerCase()
    );
    return language ? language.name : langCode;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    const newErrors: typeof errors = {};
    if (!keyName.trim()) {
      newErrors.keyName = 'Key name is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Clear any existing errors
    setErrors({});
    
    try {
      // Submit
      await onSave(keyName.trim(), defaultValue.trim());
      
      // Reset form
      setKeyName('');
      setDefaultValue('');
    } catch (err) {
      // Handle validation errors
      if (err instanceof Error) {
        setErrors({
          keyName: err.message
        });
      }
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add Translation Key"
      showCloseButton={false}
      headerActions={
        <>
          <TextButton 
            variant="secondary" 
            onClick={onClose}
            size="small"
            disabled={isLoading}
          >
            Cancel
          </TextButton>
          <TextButton
            variant="primary"
            size="small"
            onClick={handleSubmit}
            isLoading={isLoading}
          >
            Add
          </TextButton>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="translation-key-form">
        <Input
          label="Key Name"
          value={keyName}
          onChange={(e) => setKeyName(e.target.value)}
          placeholder="Enter key name"
          error={errors.keyName}
          disabled={isLoading}
          required
        />
        <TextArea
          label={`${getLanguageName(firstLanguage)} String`}
          value={defaultValue}
          onChange={(e) => setDefaultValue(e.target.value)}
          placeholder={`Enter ${getLanguageName(firstLanguage).toLowerCase()} translation`}
          error={errors.defaultValue}
          disabled={isLoading || !firstLanguage}
          rows={4}
        />
      </form>
    </Dialog>
  );
};

export default TranslationKeyDialog; 